
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FileText, Info } from 'lucide-react';
import { ChangedItem } from '@/types/quick-compare';
import { ComparisonItemCard } from './ComparisonItemCard';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface ChangedItemsTabProps {
  changedItems: ChangedItem[];
  count: number;
}

export const ChangedItemsTab: React.FC<ChangedItemsTabProps> = ({ changedItems, count }) => {
  // Filter items that have actual changes
  const itemsWithChanges = React.useMemo(() => {
    return changedItems.filter(item => {
      if (!item.changedFields || item.changedFields.length === 0) return false;
      
      // Verify that fields marked as changed actually have different values
      return item.changedFields.some(field => {
        const originalValue = item.original[field as keyof typeof item.original];
        const modifiedValue = item.modified[field as keyof typeof item.modified];
        
        // Special handling for partType to ensure it's included when changed
        if (field === 'partType') {
          return originalValue !== modifiedValue;
        }
        
        return originalValue !== modifiedValue;
      });
    });
  }, [changedItems]);
  
  // Count of items that have actual changes
  const actualChangedCount = itemsWithChanges.length;
  
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center">
          <FileText className="h-5 w-5 mr-2 text-amber-600" /> 
          Changed Items ({actualChangedCount})
        </CardTitle>
      </CardHeader>
      
      <CardContent>
        {itemsWithChanges.length > 0 ? (
          <div className="space-y-6">
            {actualChangedCount > 10 && (
              <Alert className="mb-4 bg-blue-50 border-blue-200">
                <Info className="h-4 w-4 text-blue-800" />
                <AlertDescription className="text-blue-800">
                  Showing {actualChangedCount} changed line items with their complete details. Changed fields are highlighted.
                </AlertDescription>
              </Alert>
            )}
            {itemsWithChanges.map((item, index) => (
              <ComparisonItemCard key={index} item={item} />
            ))}
          </div>
        ) : (
          <p className="text-muted-foreground">No items were changed.</p>
        )}
      </CardContent>
    </Card>
  );
};
